import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Alert,
  TablePagination,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Badge,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ScheduleCellModal from '../components/ScheduleCellModal';
import HotelOrderModal from '../components/HotelOrderModal';
import ScheduleWeekActionModal from '../components/ScheduleWeekActionModal';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useRefresh } from '../contexts/RefreshContext';
import {
  Search,
  RefreshCw,
  Check,
  X,
  RotateCcw,
  RotateCw,
  MessageSquare,
  Plus,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Ban
} from 'lucide-react';
import {
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
  addWeeks,
  addDays,
  isWithinInterval,
  startOfMonth,
  endOfMonth,
  addMonths,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isValid
} from 'date-fns';
import FixedTimeSwitch from '../components/FixedTimeSwitch';

const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

const safeParse = (value) => {
  if (!value) return new Date('');
  try {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return parseISO(value);
    return new Date(value);
  } catch {
    return new Date('');
  }
};
const safeFormat = (value, fmt, fallback = '—') => {
  const d = value instanceof Date ? value : safeParse(value);
  if (!isValid(d)) return fallback;
  try { return format(d, fmt); } catch { return fallback; }
};

const DayCell = styled(Box)(({ theme }) => ({
  border: '1px solid ' + theme.palette.divider,
  minHeight: 110,
  padding: theme.spacing(1),
  borderRadius: 12,
  background: theme.palette.background.paper,
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  position: 'relative'
}));

const StatusChip = ({ status, children, ...rest }) => {
  const map = {
    approved: { color: 'success' },
    pending: { color: 'warning' },
    refused: { color: 'error' }
  };
  const m = map[status] || { color: 'default' };
  return <Chip size="small" color={m.color} sx={{ mb: 0.5 }} {...rest} label={children || status} />;
};

const ReasonModal = ({ open, onClose, request }) => {
  if (!request) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reason for Time Off Request</DialogTitle>
      <DialogContent dividers>
        <Typography gutterBottom><strong>Employee:</strong> {request.employee_name}</Typography>
        <Typography gutterBottom><strong>Date:</strong> {request.request_date}</Typography>
        <Typography variant="body1" style={{ whiteSpace: 'pre-wrap', marginTop: 16 }}>
          {request.reason || 'No reason provided.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const RequestQuickModal = ({ open, onClose, request, onChangeStatus }) => {
  if (!request) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Time Off — {request.employee_name}</DialogTitle>
      <DialogContent dividers>
        <Typography gutterBottom><strong>Request date:</strong> {request.request_date}</Typography>
        <Typography gutterBottom><strong>Status:</strong> <StatusChip status={request.status}>{request.status}</StatusChip></Typography>
        {request.reason && (
          <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
            <strong>Reason:</strong> {request.reason}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {request.status !== 'approved' && (
          <Button color="success" variant="contained" onClick={() => onChangeStatus(request.id, 'approved')}>Approve</Button>
        )}
        {request.status !== 'refused' && (
          <Button color="error" variant="contained" onClick={() => onChangeStatus(request.id, 'refused')}>Refuse</Button>
        )}
        {request.status !== 'pending' && (
          <Button color="warning" variant="outlined" onClick={() => onChangeStatus(request.id, 'pending')}>Set Pending</Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const SchedulePage = () => {
  const { user } = useAuth();
  const { refreshKey } = useRefresh();

  const [activeTab, setActiveTab] = useState('schedule'); // 'schedule' | 'requests'

  const [weeks, setWeeks] = useState([]);
  const [currentWeek, setCurrentWeek] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const isCurrentWeek = selectedWeek === currentWeek;

  const [viewMode, setViewMode] = useState('hotel'); // 'hotel' | 'employee'

  const [scheduleData, setScheduleData] = useState({});
  const [filteredScheduleData, setFilteredScheduleData] = useState({});
  const [employeeScheduleData, setEmployeeScheduleData] = useState({});
  const [filteredEmployeeScheduleData, setFilteredEmployeeScheduleData] = useState({});

  const [hotels, setHotels] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modalInfo, setModalInfo] = useState({ open: false, hotel: '', day: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [approvedOffs, setApprovedOffs] = useState([]);

  // Time Off states
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [requestsPage, setRequestsPage] = useState(0);
  const [requestsTotalCount, setRequestsTotalCount] = useState(0);
  const [requestsPerPage, setRequestsPerPage] = useState(5);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [errorRequests, setErrorRequests] = useState('');
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [calendarRequests, setCalendarRequests] = useState([]);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [selectedRequestForReason, setSelectedRequestForReason] = useState(null);
  const [weekAction, setWeekAction] = useState(null); // 'print' | 'delete' | 'copy' | 'visibility'
  const pendingPrintRef = React.useRef(false);
  const [quickModalOpen, setQuickModalOpen] = useState(false);
  const [quickRequest, setQuickRequest] = useState(null);

  // Blocked dates states
  // blockedDatesMap: { 'yyyy-MM-dd': { id, block_date, reason, created_at, created_by_name } }
  const [blockedDatesMap, setBlockedDatesMap] = useState({});
  const [fixedTimeConfigs, setFixedTimeConfigs] = useState({});

  // Calendar controls
  const [calendarMonth, setCalendarMonth] = useState(startOfMonth(new Date()));
  const [requestViewMode, setRequestViewMode] = useState('calendar'); // 'calendar' | 'list'

  const today = new Date();
  const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
  const currentWeekEnd = endOfWeek(today, { weekStartsOn: 1 });

  // Calcula início e fim da semana selecionada
  const selectedWeekDate = useMemo(() => {
    if (!selectedWeek) return today;
    return parseISO(selectedWeek);
  }, [selectedWeek]);

  const selectedWeekStart = useMemo(
    () => startOfWeek(selectedWeekDate, { weekStartsOn: 1 }),
    [selectedWeekDate]
  );
  
  const selectedWeekEnd = useMemo(
    () => endOfWeek(selectedWeekDate, { weekStartsOn: 1 }),
    [selectedWeekDate]
  );

  // Filtra os off requests para a semana selecionada
  const selectedWeekOffs = useMemo(() => {
    if (!approvedOffs?.length) return [];
    return approvedOffs.filter(off =>
      isWithinInterval(parseISO(off.request_date), { start: selectedWeekStart, end: selectedWeekEnd })
    );
  }, [approvedOffs, selectedWeekStart, selectedWeekEnd]);

  // map por dia da semana -> [{employee_name, reason}]
  const dayOffsMap = useMemo(() => {
    const map = {};
    days.forEach(d => { map[d] = []; });
    selectedWeekOffs.forEach(off => {
      const dObj = safeParse(off.request_date);
      if (!isValid(dObj)) return;
      // JS getDay(): 0=Sun..6=Sat
      // nossa ordem days começa Monday, termina Sunday
      const mapIdxToDayName = {
        0: 'SUNDAY',
        1: 'MONDAY',
        2: 'TUESDAY',
        3: 'WEDNESDAY',
        4: 'THURSDAY',
        5: 'FRIDAY',
        6: 'SATURDAY',
      };
      const key = mapIdxToDayName[dObj.getDay()];
      if (key) {
        map[key].push({
          employee_name: off.employee_name,
          reason: off.reason || 'No reason',
          originalDate: off.request_date
        });
      }
    });
    return map;
  }, [selectedWeekOffs]);

  const pendingCount = useMemo(
    () => timeOffRequests.filter(r => r.status === 'pending').length,
    [timeOffRequests]
  );

  // carrega os pedidos de folga (approved/pending/refused) pro mês visível no calendário
  const loadTimeOffForMonth = async (monthDate) => {
    try {
      const start = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 0 });
      const end = endOfWeek(endOfMonth(monthDate), { weekStartsOn: 0 });

      const res = await api.get('/requests/off/range', {
        params: {
          start: format(start, 'yyyy-MM-dd'),
          end: format(end, 'yyyy-MM-dd'),
        }
      });
      setCalendarRequests(res.data || []);
    } catch (err) {
      console.error('Erro ao carregar time off do mês:', err);
    }
  };

  // monta a visão "por funcionário"
  const buildEmployeeSchedule = (fullSchedule, activeEmployeesList) => {
    const empMap = {};
    activeEmployeesList.forEach(emp => {
      const name = emp[1];
      empMap[name] = {};
      days.forEach(d => { empMap[name][d] = null; });
    });

    Object.keys(fullSchedule).forEach(hotel => {
      days.forEach(day => {
        (fullSchedule[hotel][day] || []).forEach(entry => {
          const name = entry.name;
          if (!empMap[name]) {
            empMap[name] = {};
            days.forEach(d => { empMap[name][d] = null; });
          }
          empMap[name][day] = { hotel, start: entry.start || null, end: entry.end || null };
        });
      });
    });
    return empMap;
  };

  const getWorkBadgeProps = (daysCount) => {
    if (daysCount >= 6) return { sx: { bgcolor: 'error.main', color: '#fff' } };
    if (daysCount === 5) return { sx: { bgcolor: 'success.main', color: '#fff' } };
    if (daysCount === 4) return { sx: { bgcolor: 'warning.dark', color: '#fff' } };
    if (daysCount === 3) return { sx: { bgcolor: 'warning.light', color: '#000' } };
    if (daysCount === 2 || daysCount === 1) return { sx: { bgcolor: '#000', color: '#fff' } };
    return { sx: { bgcolor: 'grey.500', color: '#fff' } };
  };

  // === gerar semanas futuras client-side ===
  const genFutureWeeks = (currentIsoMonday, count = 12) => {
    const base = safeParse(currentIsoMonday);
    if (!isValid(base)) return [];
    const arr = [];
    for (let i = 1; i <= count; i++) {
      const d = addWeeks(base, i);
      arr.push(format(d, 'yyyy-MM-dd'));
    }
    return arr;
  };

  const loadAvailableWeeks = async () => {
    const res = await api.get('/schedule/weeks/available');
    const { current_week, weeks: ws } = res.data || {};
    const base = current_week || format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
    const future = genFutureWeeks(base, 12);
    const uniq = Array.from(new Set([base, ...(ws || []), ...future])).sort();
    setWeeks(uniq);
    setCurrentWeek(base);
    setSelectedWeek(base);
  };

  // === Atualiza automaticamente quando "vira" a semana (domingo 20:00 ET) ===
useEffect(() => {
  let timerId;

  const refreshWeeksIfRolled = async () => {
    try {
      // Pergunta ao backend qual é a current_week agora
      const res = await api.get('/schedule/weeks/available');
      const backendCurrent = res.data?.current_week;

      if (!backendCurrent) return;

      // Se o backend mudou a semana corrente, precisamos sincronizar a UI
      if (backendCurrent !== currentWeek) {
        // Calcula qual era a "old next week" (uma semana depois da current antiga)
        // para o caso de o usuário estar visualizando a semana que acabou de virar "atual".
        const oldCurrent = currentWeek;
        // parse seguro (usa tua safeParse se já existir no arquivo; senão, Date/parseISO)
        const oldCurrentDate = safeParse ? safeParse(oldCurrent) : new Date(oldCurrent);
        const oldNextDate = addWeeks(oldCurrentDate, 1);
        const oldNext = safeFormat
          ? safeFormat(oldNextDate, 'yyyy-MM-dd')
          : oldNextDate.toISOString().slice(0, 10);

        // Gera a nova lista de semanas (misturando as do backend + client-side extras)
        const base = backendCurrent;
        const future = genFutureWeeks(base, 12);
        const uniq = Array.from(new Set([base, ...(res.data?.weeks || []), ...future])).sort();

        // Se o usuário estava na semana atual antiga OU na "próxima" antiga,
        // a seleção passa a ser a nova current (como você pediu).
        let newSelected = selectedWeek;
        if (selectedWeek === oldCurrent || selectedWeek === oldNext) {
          newSelected = backendCurrent;
        }

        setWeeks(uniq);
        setCurrentWeek(backendCurrent);
        setSelectedWeek(newSelected);

        // Recarrega os dados da semana visível
        await loadScheduleForWeek(newSelected);
      }
    } catch (e) {
      console.error('Erro ao sincronizar virada de semana:', e);
    }
  };

  // Checa imediatamente ao montar
  refreshWeeksIfRolled();

  // E checa a cada 60s para não depender de recarregar a página
  timerId = setInterval(refreshWeeksIfRolled, 60_000);

  return () => clearInterval(timerId);
  // depende de selectedWeek/currentWeek para reagir corretamente após mudanças
}, [currentWeek, selectedWeek]);

  const loadScheduleForWeek = async (weekStartStr) => {
    try {
      const [hotelRes, approvedRes, employeesRes, schedRes] = await Promise.all([
        api.get('/hotels/order'),
        api.get('/requests/off/approved'),
        api.get('/employees'),
        api.get('/schedule/week', { params: { week_start: weekStartStr } })
      ]);

      const hotelList = hotelRes.data || [];
      const employeesList = employeesRes.data || [];
      const activeEmployeesList = employeesList.filter(emp => emp[6] === 'active');

      setApprovedOffs(approvedRes.data || []);
      setEmployees(activeEmployeesList);
      setHotels(hotelList);

      const scheduleMap = schedRes.data?.schedule || {};
      const fullSchedule = {};
      hotelList.forEach(hotel => {
        fullSchedule[hotel.name] = {};
        days.forEach(day => {
          fullSchedule[hotel.name][day] = (scheduleMap[hotel.name]?.[day]) || [];
        });
      });

      setScheduleData(fullSchedule);
      setFilteredScheduleData(fullSchedule);

      const empView = buildEmployeeSchedule(fullSchedule, activeEmployeesList);
      setEmployeeScheduleData(empView);
      setFilteredEmployeeScheduleData(empView);
    
    // Carregar configurações de horário fixado
    await fetchFixedTimeConfigs(weekStartStr || currentWeek);
    } catch (err) {
      console.error('Erro ao carregar dados do schedule:', err);
    }
  };

  const loadTimeOffRequests = async (page = 1) => {
    setLoadingRequests(true);
    setErrorRequests('');
    try {
      const response = await api.get(`/requests/off?page=${page}`);
      setTimeOffRequests(response.data.requests);
      setRequestsTotalCount(response.data.total_count);
      setRequestsPerPage(response.data.per_page);
      setRequestsPage(response.data.current_page - 1);
    } catch (err) {
      setErrorRequests('Failed to load time off requests.');
      console.error('Erro ao carregar pedidos de folga:', err);
    } finally {
      setLoadingRequests(false);
    }
  };

  // carregar TODAS as datas bloqueadas
  const loadBlockedDates = useCallback(async () => {
    try {
      const res = await api.get('/blocked-dates', {
        params: {
          page: 1,
          per_page: 5000,
          search: ''
        }
      });

      const list = res.data?.data || [];
      const map = {};
      list.forEach(item => {
        const key = format(parseISO(item.block_date), 'yyyy-MM-dd');
        map[key] = {
          id: item.id,
          block_date: item.block_date,
          reason: item.reason,
          created_at: item.created_at,
          created_by_name: item.created_by_name
        };
      });

      setBlockedDatesMap(map);
    } catch (err) {
      console.error('Erro ao carregar blocked dates:', err);
    }
  }, []);

  // Carregar configurações de horário fixado
  const fetchFixedTimeConfigs = async (weekStart) => {
    try {
      const response = await api.get('/schedule/fixed-time/config', {
        params: { week_start: weekStart }
      });
      setFixedTimeConfigs(response.data.configs || {});
    } catch (error) {
      console.error('Erro ao carregar configurações de horário fixado:', error);
      setFixedTimeConfigs({});
    }
  };

  // togglar bloqueio de uma data específica
  const toggleBlockDate = useCallback(
    async (dateStr) => {
      try {
        if (blockedDatesMap[dateStr]) {
          // já está bloqueada -> desbloquear
          const idToDelete = blockedDatesMap[dateStr].id;
          await api.delete(`/blocked-dates/${idToDelete}`);
          await loadBlockedDates();
        } else {
          // não está bloqueada -> bloquear
          await api.post('/blocked-dates', {
            block_date: dateStr,
            reason: ''
          });
          await loadBlockedDates();
        }
      } catch (err) {
        console.error('Erro ao alternar bloqueio de data:', err);
        window.alert('Error toggling blocked date.');
      }
    },
    [blockedDatesMap, loadBlockedDates]
  );

  useEffect(() => {
    loadAvailableWeeks().then(() => {
      loadTimeOffRequests(1);
    });
    loadBlockedDates();
    const handleOpenOrderModal = () => setOrderModalOpen(true);
    window.addEventListener('openHotelOrderModal', handleOpenOrderModal);
    return () => window.removeEventListener('openHotelOrderModal', handleOpenOrderModal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    if (!selectedWeek) return;
    (async () => {
      await loadScheduleForWeek(selectedWeek);
      if (pendingPrintRef.current) {
        pendingPrintRef.current = false;
        window.print();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek]);

  // Botões da barra de cima (print/delete/copy/visibility): todos abrem o mesmo
  // modal aqui, que é quem sabe quais semanas existem.
  useEffect(() => {
    const openAction = (e) => setWeekAction(e?.detail?.action || null);
    window.addEventListener('scheduleAction', openAction);
    return () => window.removeEventListener('scheduleAction', openAction);
  }, []);

  useEffect(() => {
    loadTimeOffForMonth(calendarMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarMonth]);

  useEffect(() => {
    if (selectedWeek) {
      fetchFixedTimeConfigs(selectedWeek);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek]);
  useEffect(() => {
    if (selectedWeek) {
      fetchFixedTimeConfigs(selectedWeek);
    }
  }, [selectedWeek]);


  useEffect(() => {
    const query = (searchQuery || '').trim().toLowerCase();

    if (!query) {
      setFilteredScheduleData(scheduleData);
    } else {
      const filtered = {};
      Object.keys(scheduleData).forEach(hotel => {
        const hotelMatches = hotel.toLowerCase().includes(query);
        let employeeMatches = false;
        const filteredDays = {};
        days.forEach(day => {
          const emps = scheduleData[hotel][day] || [];
          const filteredEmployees = emps.filter(e => e.name.toLowerCase().includes(query));
          if (filteredEmployees.length > 0) {
            employeeMatches = true;
            filteredDays[day] = filteredEmployees;
          } else if (hotelMatches) {
            filteredDays[day] = emps;
          } else {
            filteredDays[day] = [];
          }
        });
        if (hotelMatches || employeeMatches) filtered[hotel] = filteredDays;
      });
      setFilteredScheduleData(filtered);
    }

    if (!query) {
      setFilteredEmployeeScheduleData(employeeScheduleData);
    } else {
      const filteredEmp = {};
      Object.keys(employeeScheduleData).forEach(empName => {
        if (empName.toLowerCase().includes(query)) {
          filteredEmp[empName] = employeeScheduleData[empName];
        }
      });
      setFilteredEmployeeScheduleData(filteredEmp);
    }
  }, [searchQuery, scheduleData, employeeScheduleData]);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const clearSearch = () => setSearchQuery('');
  const handleChangePage = (event, newPage) => loadTimeOffRequests(newPage + 1);

  const handleRequestStatusChange = async (requestId, status) => {
    try {
      await api.put(`/requests/off/${requestId}`, { status });
      loadTimeOffRequests(requestsPage + 1);
      await loadTimeOffForMonth(calendarMonth);
      if (selectedWeek) await loadScheduleForWeek(selectedWeek);
      if (quickRequest && quickRequest.id === requestId) {
        setQuickRequest({ ...quickRequest, status });
      }
    } catch (err) {
      console.error(`Erro ao ${status} pedido:`, err);
      alert(`Failed to ${status} request.`);
    }
  };

  const fmtWeekRange = useMemo(() => {
    if (!selectedWeek) return '';
    const start = safeParse(selectedWeek);
    if (!isValid(start)) return selectedWeek;
    const end = addDays(start, 6);
    return `${safeFormat(start, 'MMM d, yyyy')} — ${safeFormat(end, 'MMM d, yyyy')}`;
  }, [selectedWeek]);

  const goPrevWeek = () => {
    if (!weeks.length || !selectedWeek) return;
    const idx = weeks.indexOf(selectedWeek);
    if (idx > 0) setSelectedWeek(weeks[idx - 1]);
  };
  const goNextWeek = () => {
    if (!weeks.length || !selectedWeek) return;
    const idx = weeks.indexOf(selectedWeek);
    if (idx >= weeks.length - 1) {
      const tailStart = weeks[weeks.length - 1];
      const extra = genFutureWeeks(tailStart, 4);
      const merged = Array.from(new Set([...weeks, ...extra])).sort();
      setWeeks(merged);
      setSelectedWeek(merged[idx + 1] || merged[idx]);
    } else {
      setSelectedWeek(weeks[idx + 1]);
    }
  };

  if (user?.role_id !== 2 && user?.role_id !== 3) {
    return (
      <Box p={2}>
        <Typography variant="h6" color="error">
          Access Denied: Only Admins and Managers can view this page.
        </Typography>
      </Box>
    );
  }

  const renderEmployeeCell = (data) => {
    if (!data) return (
      <Typography variant="body2" sx={{ fontSize: '12px', lineHeight: 1.3, color: 'error.main' }}>
        OFF
      </Typography>
    );
    return (
      <Typography variant="body2" sx={{ fontSize: '12px', lineHeight: 1.3 }}>
        {data.hotel}
      </Typography>
    );
  };

  return (
    <Box p={1} maxWidth={'1100px'} mx="auto">
      {/* Tabs */}
      <Box className="print-hide" sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{ mb: 1, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.9)', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}
        >
          <Tab value="schedule" label={<Box display="flex" alignItems="center" gap={1}><span>Schedule</span></Box>} />
          <Tab value="requests" label={
            <Badge color={pendingCount > 0 ? 'warning' : 'default'} badgeContent={pendingCount || 0} max={99} overlap="circular">
              <Box display="flex" alignItems="center" gap={1}><span>Time Off</span></Box>
            </Badge>
          } />
        </Tabs>
      </Box>
          
      {/* === SCHEDULE TAB === */}
      {activeTab === 'schedule' && (
        <>
          {/* Week selector */}
          <Box
            className="print-hide"
            sx={{
              mb: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
              backgroundColor: 'white',
              borderRadius: 2,
              p: 1.5,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                onClick={goPrevWeek}
                disabled={!weeks.length || weeks.indexOf(selectedWeek) <= 0}
                size="small"
                sx={{ backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#e0e0e0' }, borderRadius: 1.5 }}
              >
                <ChevronLeft size={18} />
              </IconButton>

              <Box display="flex" alignItems="center" gap={1}>
                <CalendarIcon size={18} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {fmtWeekRange || '—'}
                </Typography>
                {isCurrentWeek && <Chip size="small" color="primary" label="Current Week" />}
              </Box>

              <IconButton
                onClick={goNextWeek}
                size="small"
                sx={{ backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#e0e0e0' }, borderRadius: 1.5 }}
              >
                <ChevronRight size={18} />
              </IconButton>
            </Box>

            <Box>
              <TextField
                select
                SelectProps={{ native: true }}
                size="small"
                label="Jump to week"
                value={selectedWeek || ''}
                onChange={(e) => setSelectedWeek(e.target.value)}
                sx={{ minWidth: 220, backgroundColor: 'white' }}
              >
                {(weeks || []).map(w => {
                  const start = safeParse(w);
                  const end = addDays(start, 6);
                  return (
                    <option key={w} value={w}>
                      {safeFormat(start, 'MMM d')} — {safeFormat(end, 'MMM d, yyyy')}
                      {w === currentWeek ? ' (current)' : ''}
                    </option>
                  );
                })}
              </TextField>
            </Box>
          </Box>

          {/* Visão */}
          <Box className="print-hide" sx={{ mb: 1 }}>
            <Tabs
              value={viewMode}
              onChange={(_, v) => setViewMode(v)}
              variant="fullWidth"
              sx={{ mb: 2, borderRadius: 2, backgroundColor: 'rgba(255, 255, 255, 0.9)', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}
            >
              <Tab value="hotel" label="By Hotel" />
              <Tab value="employee" label="By Employee" />
            </Tabs>
          </Box>

          {/* Busca */}
          <Box maxWidth="1000" mb={1} className="print-hide">
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder={viewMode === 'hotel' ? 'Search hotels or employees...' : 'Search employees...'}
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <Tooltip title="Clear search">
                      <IconButton size="small" onClick={clearSearch} edge="end">
                        <RefreshCw size={16} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
              sx={{
                width: { 100: '100%' },
                bgcolor: 'white',
                borderRadius: 5,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' } }
              }}
            />
          </Box>

          {/* Tabela principal */}
          <Box
            className="printable-content"
            sx={{
              overflowX: 'auto',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              maxWidth: 1300,
              margin: '0 auto',
              width: '100%',
              maxHeight: '70vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '4px' },
              '@media print': { maxHeight: 'none', overflow: 'visible', boxShadow: 'none' }
            }}
          >
            <TableContainer component={Paper} sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120, px: 1, py: 1 }}>
                      {viewMode === 'hotel' ? 'RESORT' : 'EMPLOYEE'}
                    </TableCell>
                    {days.map((day) => (
                      <TableCell key={day} sx={{ fontWeight: 'bold', minWidth: 110, px: 1, py: 1 }}>
                        {day}
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Linha de switches para horário fixado */}
                  {viewMode === 'hotel' && (
                    <TableRow>
                      <TableCell
                        sx={{
                        background: 'linear-gradient(90deg, #f0f9ff 0%, #e0f2fe 100%)',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '14px',
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        color: '#0284c7',
                        px: 1,
                        py: 0.75,
                        borderTop: '1px solid rgba(2,132,199,0.2)',
                        borderBottom: '1px solid rgba(2,132,199,0.2)',
                        position: 'static',
                        left: 'auto',
                        zIndex: 'auto',
                        lineHeight: 1.2
                }}
              >
                FIXED TIME
              </TableCell>
              {days.map((day, idx) => {
                // Calcular a data específica para este dia
                const weekStartDate = selectedWeek ? new Date(selectedWeek + 'T00:00:00') : new Date();
                const dayDate = new Date(weekStartDate);
                dayDate.setDate(dayDate.getDate() + idx);
                const dateStr = !isNaN(dayDate.getTime()) ? dayDate.toISOString().split('T')[0] : '';
                
                const config = fixedTimeConfigs[day] || { date: dateStr, is_enabled: false };
                
                return (
                        <TableCell
                          key={day}
                          sx={{
                            background: 'linear-gradient(90deg, #f0f9ff 0%, #e0f2fe 100%)',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                            borderRadius: '8px',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            px: 1,
                            py: 0.75,
                            borderTop: '1px solid rgba(2,132,199,0.2)',
                            borderBottom: '1px solid rgba(2,132,199,0.2)',
                          }}
                        >
                          {dateStr && (
                            <FixedTimeSwitch
                              date={config.date || dateStr}
                              dayOfWeek={day}
                              initialEnabled={config.is_enabled}
                              onChange={(newValue) => {
                                setFixedTimeConfigs(prev => ({
                                  ...prev,
                                  [day]: { ...config, is_enabled: newValue }
                                }));
                              }}
                              disabled={!isCurrentWeek}
                            />
                          )}
                        </TableCell>
                );
              })}
            </TableRow>
          )}



                  {/* NOVA LINHA "OFF" */}
                  <TableRow>
                    <TableCell
                  sx={{
                    background: 'linear-gradient(90deg, #fff5f5ff 0%, #ffe6e6ff 100%)',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                    borderRadius: '8px',
                    fontWeight: 700,
                    fontSize: '14px',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    color: '#f10909ff', // rosa escuro
                    px: 1,
                    py: 0.75,

                    // borda suave pra separar do resto
                    borderTop: '1px solid rgba(214,51,132,0.2)',
                    borderBottom: '1px solid rgba(214,51,132,0.2)',

                    // remove sticky pra não travar scroll
                    position: 'static',
                    left: 'auto',
                    zIndex: 'auto',
                     lineHeight: 1.2
                  }}
                >
                  REQUESTS
                </TableCell>

                    {days.map((day) => {
                      const offs = dayOffsMap[day] || [];
                      return (
                       <TableCell
  key={day}
  sx={{
    background: 'linear-gradient(90deg, #fff5f5ff 0%, #ffe6e6ff 100%)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
    borderRadius: '8px',
    verticalAlign: 'top',

    px: 1,
    py: 0.75,

    borderTop: '1px solid rgba(214,51,132,0.2)',
    borderBottom: '1px solid rgba(214,51,132,0.2)',
  }}
>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              rowGap: '4px',
                              columnGap: '4px'
                            }}
                          >
                            {offs.slice(0, 3).map((o, idx) => (
                              <Tooltip
                                key={idx}
                                title={o.reason || 'No reason'}
                              >
                                <Chip
                                  size="small"
                                  label={o.employee_name}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.6rem',
                                    fontWeight: 600,
                                    borderRadius: '6px',
                                    px: '4px',
                                    backgroundColor: isCurrentWeek ? 'error.main' : 'warning.main',
                                    color: '#fff',
                                    '& .MuiChip-label': {
                                      px: '4px',
                                      py: 0,
                                      lineHeight: 1.2,
                                    }
                                  }}
                                />
                              </Tooltip>
                            ))}

                            {offs.length > 3 && (
                              <Chip
                                size="small"
                                label={`+${offs.length - 3}`}
                                sx={{
                                  height: 20,
                                  fontSize: '0.6rem',
                                  fontWeight: 600,
                                  borderRadius: '6px',
                                  px: '4px',
                                  backgroundColor: isCurrentWeek ? 'error.main' : 'warning.main',
                                  color: '#fff',
                                  '& .MuiChip-label': {
                                    px: '4px',
                                    py: 0,
                                    lineHeight: 1.2,
                                  }
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {viewMode === 'hotel' ? (
                    Object.keys(filteredScheduleData).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          No results found for "{searchQuery}"
                        </TableCell>
                      </TableRow>
                    ) : (
                      Object.keys(filteredScheduleData)
                        .sort((a, b) => {
                          const hotelA = hotels.find(h => h.name === a);
                          const hotelB = hotels.find(h => h.name === b);
                          const orderA = hotelA?.display_order || 999;
                          const orderB = hotelB?.display_order || 999;
                          if (orderA !== orderB) return orderA - orderB;
                          return a.localeCompare(b);
                        })
                        .map((hotel) => (
                          <TableRow key={hotel}>
                            <TableCell sx={{ fontWeight: 'bold', px: 1, py: 1 }}>{hotel}</TableCell>
                            {days.map((day) => (
                              <TableCell key={day} sx={{ px: 1, py: 0.5 }}>
                                <Box display="flex" alignItems="flex-start" gap={0.5}>
                                  <Box className="print-hide">
                                    <IconButton
                                      onClick={() => setModalInfo({ open: true, hotel, day })}
                                      size="small"
                                      sx={{ p: 0.1, mt: -0.5 }}
                                    >
                                      <Plus size={13} />
                                    </IconButton>
                                  </Box>
                                  <Box display="flex" flexDirection="column" flex={1}>
                                    {(filteredScheduleData[hotel][day] || []).map((e, idx) => (
                                      <Typography key={idx} variant="body2" sx={{ fontSize: '12px', lineHeight: 1.3 }}>
                                        {e.name}
                                      </Typography>
                                    ))}
                                  </Box>
                                </Box>
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                    )
                  ) : (
                    Object.keys(filteredEmployeeScheduleData).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          No employees found for "{searchQuery}"
                        </TableCell>
                      </TableRow>
                    ) : (
                      Object.keys(filteredEmployeeScheduleData)
                        .sort((a, b) => a.localeCompare(b))
                        .map((empName) => {
                          const workDays = days.reduce(
                            (acc, d) => acc + (filteredEmployeeScheduleData[empName][d] ? 1 : 0),
                            0
                          );
                          return (
                            <TableRow key={empName}>
                              <TableCell sx={{ fontWeight: 'bold', px: 1, py: 1 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Chip label={workDays} size='small' {...getWorkBadgeProps(workDays)} />
                                  <Typography variant="body2" sx={{ fontSize: '13px' }}>
                                    <strong>{empName}</strong>
                                  </Typography>
                                </Box>
                              </TableCell>
                              {days.map((day) => (
                                <TableCell key={day} sx={{ px: 1, py: 0.5 }}>
                                  {renderEmployeeCell(filteredEmployeeScheduleData[empName][day])}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'none', '@media print': { display: 'block', mt: 3, pageBreakInside: 'avoid' } }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
                Active Employees ({employees.length})
              </Typography>
              <Typography sx={{ fontSize: '12px', textAlign: 'justify', lineHeight: 1.4, p: 1, border: '1px solid #ddd' }}>
                {employees.map(emp => emp[1]).join(', ')}
              </Typography>
            </Box>
          </Box>

          <ScheduleCellModal
            open={modalInfo.open}
            hotel={modalInfo.hotel}
            day={modalInfo.day}
            weekStart={selectedWeek}
            isCurrentWeek={isCurrentWeek}
            onClose={() => setModalInfo({ open: false, hotel: '', day: '' })}
            onSave={() => { loadScheduleForWeek(selectedWeek); }}
          />

          <HotelOrderModal
            open={orderModalOpen}
            onClose={() => setOrderModalOpen(false)}
            onSave={() => { loadScheduleForWeek(selectedWeek); setOrderModalOpen(false); }}
          />

          <ScheduleWeekActionModal
            open={!!weekAction}
            action={weekAction}
            weeks={weeks}
            currentWeek={currentWeek}
            defaultWeek={selectedWeek}
            onClose={() => setWeekAction(null)}
            onPrint={(week) => {
              if (week === selectedWeek) { window.print(); return; }
              pendingPrintRef.current = true;
              setSelectedWeek(week);
            }}
            onDone={(action, week) => {
              if (action === 'delete' || action === 'copy') loadScheduleForWeek(selectedWeek);
            }}
          />
        </>
      )}

      {/* === TIME OFF TAB === */}
      {activeTab === 'requests' && (
        <>
          <Box
            className="print-hide"
            sx={{
              mb: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            {/* Month selector + title */}
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                padding: '8px 16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
              }}
            >
              <IconButton
                onClick={() => setCalendarMonth(addMonths(calendarMonth, -1))}
                size="small"
                sx={{ backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#e0e0e0' }, borderRadius: 1.5 }}
              >
                <ChevronLeft size={18} />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  minWidth: { xs: 140, sm: 180 },
                  textAlign: 'center',
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  fontWeight: 600
                }}
              >
                {format(calendarMonth, 'MMMM yyyy')}
              </Typography>
              <IconButton
                onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                size="small"
                sx={{ backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#e0e0e0' }, borderRadius: 1.5 }}
              >
                <ChevronRight size={18} />
              </IconButton>
            </Box>

            {/* Toggle Calendar/List */}
            <ToggleButtonGroup
              size="small"
              value={requestViewMode}
              exclusive
              onChange={(_, v) => v && setRequestViewMode(v)}
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0',
                '& .MuiToggleButton-root': {
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: 1.5,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  '&.Mui-selected': {
                    backgroundColor: '#1976d2',
                    color: 'white',
                    '&:hover': { backgroundColor: '#1565c0' }
                  }
                }
              }}
            >
              <ToggleButton value="calendar">Calendar</ToggleButton>
              <ToggleButton value="list">List</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Calendar OR List */}
          {requestViewMode === 'calendar' ? (
            <TimeOffCalendar
              timeOffRequests={calendarRequests}
              loadingRequests={loadingRequests}
              errorRequests={errorRequests}
              onChangeStatus={handleRequestStatusChange}
              quickRequest={quickRequest}
              setQuickRequest={setQuickRequest}
              quickModalOpen={quickModalOpen}
              setQuickModalOpen={setQuickModalOpen}
              calendarMonth={calendarMonth}
              blockedDatesMap={blockedDatesMap}
              onToggleBlockDate={toggleBlockDate}
            />
          ) : (
            <TimeOffList
              timeOffRequests={timeOffRequests}
              loadingRequests={loadingRequests}
              errorRequests={errorRequests}
              requestsTotalCount={requestsTotalCount}
              requestsPerPage={requestsPerPage}
              requestsPage={requestsPage}
              onChangePage={handleChangePage}
              onApprove={(id) => handleRequestStatusChange(id, 'approved')}
              onRefuse={(id) => handleRequestStatusChange(id, 'refused')}
              onOpenReason={(req) => { setSelectedRequestForReason(req); setReasonModalOpen(true); }}
            />
          )}

          <ReasonModal
            open={reasonModalOpen}
            onClose={() => { setReasonModalOpen(false); setSelectedRequestForReason(null); }}
            request={selectedRequestForReason}
          />
          <RequestQuickModal
            open={quickModalOpen}
            onClose={() => setQuickModalOpen(false)}
            request={quickRequest}
            onChangeStatus={(id, status) => {
              handleRequestStatusChange(id, status);
              setQuickModalOpen(false);
            }}
          />
        </>
      )}
    </Box>
  );
};

const TimeOffCalendar = ({
  timeOffRequests,
  loadingRequests,
  errorRequests,
  onChangeStatus,
  quickRequest,
  setQuickRequest,
  quickModalOpen,
  setQuickModalOpen,
  calendarMonth,
  blockedDatesMap,
  onToggleBlockDate
}) => {
  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(calendarMonth), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(calendarMonth), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [calendarMonth]);

  const requestsByDay = useMemo(() => {
    const map = {};
    (timeOffRequests || []).forEach((r) => {
      const d = safeParse(r.request_date);
      if (!isValid(d)) return;
      const key = format(d, 'yyyy-MM-dd');
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return map;
  }, [timeOffRequests]);

  return (
    <Box sx={{ mb: 2 }}>
      {errorRequests && <Alert severity="error" sx={{ mb: 2 }}>{errorRequests}</Alert>}
      {loadingRequests ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <>
          {/* Headers Sun..Sat */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1 }}>
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((w) => (
              <Typography key={w} variant="overline" sx={{ textAlign: 'center', color: 'text.secondary' }}>{w}</Typography>
            ))}
          </Box>

          {/* Calendar grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
            {calendarDays.map((d) => {
              const key = format(d, 'yyyy-MM-dd');
              const list = requestsByDay[key] || [];
              const muted = !isSameMonth(d, calendarMonth);
              const isBlocked = !!blockedDatesMap[key];

              return (
                <DayCell
                  key={key}
                  sx={{
                    opacity: muted ? 0.5 : 1,
                    borderColor: isBlocked ? 'error.main' : undefined,
                    backgroundColor: isBlocked ? 'rgba(255,0,0,0.07)' : undefined
                  }}
                >
                  {/* Botão block/unblock */}
                  <Tooltip title={isBlocked ? 'Unblock this date' : 'Block this date'}>
                    <IconButton
                      size="small"
                      onClick={() => onToggleBlockDate(key)}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        borderRadius: 1.5,
                        padding: '2px 4px',
                        backgroundColor: isBlocked ? 'error.main' : '#f5f5f5',
                        color: isBlocked ? '#fff' : 'inherit',
                        '&:hover': {
                          backgroundColor: isBlocked ? 'error.dark' : '#e0e0e0'
                        }
                      }}
                    >
                      <Ban size={14} />
                    </IconButton>
                  </Tooltip>

                  {/* Dia do mês */}
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 10,
                      fontWeight: 700
                    }}
                  >
                    {format(d, 'd')}
                  </Typography>

                  {/* Listagem de pedidos de folga */}
                  <Box mt={4} display="flex" flexDirection="column" gap={0.5}>
                    {list.length === 0 ? (
                      <Typography variant="caption" sx={{ color: 'text.disabled', textAlign: 'center', mt: 2 }}>
                        {isBlocked ? 'BLOCKED' : '—'}
                      </Typography>
                    ) : (
                      list.map((r) => (
                        <Chip
                          key={r.id}
                          size="small"
                          label={`${r.employee_name}`}
                          onClick={() => { setQuickRequest(r); setQuickModalOpen(true); }}
                          color={r.status === 'approved' ? 'success' : r.status === 'pending' ? 'warning' : 'error'}
                          variant={(function(){
                            const d2 = safeParse(r.request_date);
                            return isValid(d2) && isSameDay(d2, new Date()) ? 'filled' : 'outlined';
                          })()}
                        />
                      ))
                    )}
                  </Box>
                </DayCell>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};

const TimeOffList = ({
  timeOffRequests,
  loadingRequests,
  errorRequests,
  requestsTotalCount,
  requestsPerPage,
  requestsPage,
  onChangePage,
  onApprove,
  onRefuse,
  onOpenReason
}) => {
  return (
    <Box
      className="print-hide"
      sx={{
        mt: 1,
        overflowX: 'auto',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        maxWidth: 1000,
        width: '100%',
        maxHeight: '60vh',
        overflowY: 'auto',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '4px' }
      }}
    >
      {errorRequests && <Alert severity="error" sx={{ mb: 2 }}>{errorRequests}</Alert>}
      <TableContainer component={Paper} sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 100, px: 1, py: 1 }}>EMPLOYEE</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 100, px: 1, py: 1 }}>REQUEST DATE</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 100, px: 1, py: 1 }}>REQUESTED AT</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 100, px: 1, py: 1 }}>STATUS</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 100, px: 1, py: 1 }}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingRequests ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : timeOffRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  No pending requests found.
                </TableCell>
              </TableRow>
            ) : (
              timeOffRequests.map((req) => (
                <TableRow key={req.id}>
                  <TableCell>
                    {req.employee_name}
                    {req.reason && (
                      <Tooltip title="View Reason">
                        <IconButton size="small" sx={{ ml: 1 }} onClick={() => onOpenReason(req)}>
                          <MessageSquare size={14} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{req.request_date}</TableCell>
                  <TableCell>{req.created_at}</TableCell>
                  <TableCell>
                    {req.status === 'approved' ? <Chip label="approved" color="success" size="small" /> :
                     req.status === 'refused'  ? <Chip label="refused"  color="error"   size="small" /> :
                                                  <Chip label="pending"  color="warning" size="small" />}
                  </TableCell>
                  <TableCell align="right">
                    {req.status === 'pending' && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton color="success" size="small" onClick={() => onApprove(req.id)}>
                            <Check />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Refuse">
                          <IconButton color="error" size="small" onClick={() => onRefuse(req.id)}>
                            <X />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {req.status === 'refused' && (
                      <Tooltip title="Revert to Approved">
                        <IconButton color="success" size="small" onClick={() => onApprove(req.id)}>
                          <RotateCcw size={16} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {req.status === 'approved' && (
                      <Tooltip title="Revert to Refused">
                        <IconButton color="error" size="small" onClick={() => onRefuse(req.id)}>
                          <RotateCw size={16} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        className="print-hide"
        rowsPerPageOptions={[]}
        component="div"
        count={requestsTotalCount}
        rowsPerPage={requestsPerPage}
        page={requestsPage}
        onPageChange={onChangePage}
        sx={{ backgroundColor: 'white', borderTop: '1px solid #e0e0e0' }}
      />
    </Box>
  );
};

export default SchedulePage;